import { useEffect, useMemo, useState } from "react";
import {
  AreaChart, Area, LineChart, Line, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid,
} from "recharts";
import { Eye, MousePointerClick, Percent, Crosshair, Check, RefreshCw, ChevronDown, Info } from "lucide-react";
import { isDemoMode } from "@/lib/demoMode";

type Daily = { date: string; clicks: number; impressions: number; ctr: number; position: number };
type GscData = {
  clicks: number; impressions: number; ctr: number; position: number;
  siteUrl?: string; dateRange?: string; daily?: Daily[];
};

interface Props {
  gscIsConnected: boolean;
  gscPropertySelected: boolean;
  gscProperty: string | null;
  gscLoading: boolean;
  gscError: string | null;
  gscData: GscData | null;
  gscDateRange: "24h" | "7d" | "28d" | "90d";
  gscConnectedAt: string | null;
  onConnect: () => void;
  onDisconnect: () => void;
  onChangeProperty: () => void;
  onSelectProperty: () => void;
  onDateRangeChange: (r: "24h" | "7d" | "28d" | "90d") => void;
}

const COLORS = {
  impressions: "#00FF88",
  clicks: "#8B5CF6",
  ctr: "#F59E0B",
  position: "#06B6D4",
};

function useCountUp(target: number, duration = 900) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    let raf = 0;
    const start = performance.now();
    const from = 0;
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setVal(from + (target - from) * eased);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return val;
}

function MetricTile({
  metricKey, label, Icon, color, value, format, trend, sparkData, active, onToggle,
}: {
  metricKey: "impressions" | "clicks" | "ctr" | "position";
  label: string;
  Icon: any;
  color: string;
  value: number;
  format: (n: number) => string;
  trend: number | null; // % change
  sparkData: Array<{ x: number; y: number }>;
  active: boolean;
  onToggle: () => void;
}) {
  const animated = useCountUp(value);
  const trendColor = trend == null ? "#94a3b8" : trend >= 0 ? "#00FF88" : "#ef4444";
  const trendArrow = trend == null ? "" : trend >= 0 ? "↑" : "↓";

  return (
    <button
      type="button"
      data-tour-id={`gsc-${metricKey}`}
      onClick={onToggle}
      className="group relative overflow-hidden rounded-2xl border text-left transition-all hover:scale-[1.01] p-2.5 sm:p-4 min-h-[118px] sm:min-h-[168px]"
      style={{
        background: `linear-gradient(160deg, ${color}14 0%, rgba(10,15,30,0.6) 60%)`,
        borderColor: active ? `${color}55` : "rgba(255,255,255,0.06)",
        boxShadow: active ? `0 0 24px ${color}22, inset 0 0 0 1px ${color}22` : "none",
      }}
    >
      <div className="flex items-start justify-between">
        <div
          className="flex h-7 w-7 sm:h-9 sm:w-9 items-center justify-center rounded-lg"
          style={{ background: `${color}1f`, border: `1px solid ${color}33`, color }}
        >
          <Icon size={16} />
        </div>
        <div
          className="flex h-5 w-5 items-center justify-center rounded-full transition-opacity"
          style={{
            background: active ? `${color}` : "rgba(255,255,255,0.06)",
            opacity: active ? 1 : 0.4,
            color: "#0A0F1E",
          }}
        >
          {active && <Check size={12} strokeWidth={3} />}
        </div>
      </div>
      <div className="mt-1.5 text-[9.5px] sm:text-[10px] font-bold uppercase tracking-[1.2px] text-white/55 flex items-center gap-1">
        {label} <Info size={10} className="opacity-60" />
      </div>
      <div
        className="mt-0.5 text-[21px] sm:text-[34px] font-extrabold leading-tight tracking-tight tabular-nums"
        style={{ color, textShadow: `0 0 18px ${color}55` }}
      >
        {format(animated)}
      </div>
      {trend != null && (
        <div className="mt-0.5 text-[10px] sm:text-[11px] font-semibold leading-tight" style={{ color: trendColor }}>
          {trendArrow} {Math.abs(trend).toFixed(1)}% <span className="text-white/40 font-medium">vs previous period</span>
        </div>
      )}
      {/* Sparkline */}
      <div className="mt-1.5 -mx-2 h-[32px] sm:mt-2 sm:h-[42px]">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={sparkData} margin={{ top: 4, right: 4, left: 4, bottom: 0 }}>
            <defs>
              <linearGradient id={`spark-${metricKey}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity={0.5} />
                <stop offset="100%" stopColor={color} stopOpacity={0} />
              </linearGradient>
            </defs>
            <Area
              type="monotone"
              dataKey="y"
              stroke={color}
              strokeWidth={1.6}
              fill={`url(#spark-${metricKey})`}
              isAnimationActive
              animationDuration={1200}
              dot={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </button>
  );
}

function ToggleSwitch({ on, color, label, onChange }: { on: boolean; color: string; label: string; onChange: () => void }) {
  return (
    <button
      onClick={onChange}
      className="flex items-center gap-2.5 select-none"
      type="button"
    >
      <span
        className="relative inline-flex h-[22px] w-[40px] items-center rounded-full transition-colors"
        style={{ background: on ? color : "rgba(255,255,255,0.12)" }}
      >
        <span
          className="inline-block h-[16px] w-[16px] rounded-full bg-white shadow transition-transform"
          style={{ transform: on ? "translateX(21px)" : "translateX(3px)" }}
        />
      </span>
      <span className="text-[12.5px] font-semibold text-white/80">{label}</span>
    </button>
  );
}

function GLogo({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3l5.7-5.7C34.3 6 29.4 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z"/>
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3.1 0 5.8 1.2 7.9 3l5.7-5.7C34.3 6 29.4 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
      <path fill="#4CAF50" d="M24 44c5.3 0 10.1-2 13.7-5.3l-6.3-5.3C29.4 35 26.8 36 24 36c-5.3 0-9.7-3.4-11.3-8l-6.5 5C9.6 39.6 16.2 44 24 44z"/>
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.4l6.3 5.3C41.2 35.7 44 30.3 44 24c0-1.3-.1-2.3-.4-3.5z"/>
    </svg>
  );
}

export default function GscPremiumCard(props: Props) {
  const {
    gscIsConnected, gscPropertySelected, gscProperty, gscLoading, gscError, gscData, gscDateRange, gscConnectedAt,
    onConnect, onDisconnect, onChangeProperty, onSelectProperty, onDateRangeChange,
  } = props;

  const [visible, setVisible] = useState({ impressions: true, clicks: true, ctr: true, position: true });

  const daily = gscData?.daily ?? [];

  // Trend % vs previous same-length period (using the daily array)
  const trends = useMemo(() => {
    if (daily.length < 4) return { impressions: null as number | null, clicks: null, ctr: null, position: null };
    const half = Math.floor(daily.length / 2);
    const prev = daily.slice(0, half);
    const curr = daily.slice(daily.length - half);
    const sum = (arr: Daily[], k: keyof Daily) => arr.reduce((a, b) => a + (b[k] as number || 0), 0);
    const avg = (arr: Daily[], k: keyof Daily) => arr.length ? sum(arr, k) / arr.length : 0;
    const pct = (a: number, b: number) => (b === 0 ? null : ((a - b) / b) * 100);
    return {
      impressions: pct(sum(curr, "impressions"), sum(prev, "impressions")),
      clicks: pct(sum(curr, "clicks"), sum(prev, "clicks")),
      ctr: pct(avg(curr, "ctr"), avg(prev, "ctr")),
      // Position: lower is better → invert
      position: (() => {
        const a = avg(curr, "position"); const b = avg(prev, "position");
        if (b === 0) return null;
        return ((b - a) / b) * 100;
      })(),
    };
  }, [daily]);

  const sparkData = (key: keyof Daily) =>
    daily.map((d, i) => ({ x: i, y: d[key] as number }));

  const chartData = daily.map((d) => {
    const dt = new Date(d.date);
    return {
      date: d.date,
      label: `${dt.toLocaleString("en-US", { month: "short" })} ${dt.getDate()}`,
      impressions: d.impressions,
      clicks: d.clicks,
      ctr: +(d.ctr * 100).toFixed(2),
      position: d.position,
    };
  });

  const lastUpdated = useMemo(() => {
    if (!gscConnectedAt) return null;
    const diffMin = Math.max(1, Math.round((Date.now() - new Date(gscConnectedAt).getTime()) / 60000));
    if (diffMin < 60) return `${diffMin} min ago`;
    const h = Math.round(diffMin / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.round(h / 24)}d ago`;
  }, [gscConnectedAt]);

  return (
    <div
      data-tour-id="gsc-card"
      className="mb-[18px] rounded-2xl p-3 sm:p-[22px]"
      style={{
        background: "linear-gradient(180deg, #0A0F1E 0%, #070B17 100%)",
        border: "1px solid rgba(255,255,255,0.06)",
        boxShadow: "0 20px 60px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.03)",
        backdropFilter: "blur(20px)",
      }}
    >
      {/* TOP BAR */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="relative inline-flex h-2.5 w-2.5">
              <span className="absolute inset-0 animate-ping rounded-full" style={{ background: COLORS.impressions, opacity: 0.5 }} />
              <span className="relative inline-block h-2.5 w-2.5 rounded-full" style={{ background: COLORS.impressions, boxShadow: `0 0 10px ${COLORS.impressions}` }} />
            </span>
            <span className="text-[12.5px] text-white/70">
              Connected to: <span className="font-semibold text-white">{gscData?.siteUrl || gscProperty || "—"}</span>
            </span>
          </div>
          <span
            className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-[11.5px] font-semibold"
            style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.85)" }}
          >
            <GLogo size={13} /> Google Search Console
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {gscIsConnected ? (
            !isDemoMode() && (
              <>
                <button
                  onClick={onConnect}
                  className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11.5px] font-semibold text-white/85 transition-colors hover:bg-white/10"
                  style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}
                >
                  <RefreshCw size={12} /> Reconnect GSC
                </button>
                <button
                  onClick={onChangeProperty}
                  className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11.5px] font-semibold text-white/85 transition-colors hover:bg-white/10"
                  style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}
                >
                  Change Property <ChevronDown size={12} />
                </button>
                <button
                  onClick={onDisconnect}
                  className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11.5px] font-semibold text-red-400 transition-colors hover:bg-red-500/10"
                  style={{ background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.25)" }}
                >
                  Disconnect
                </button>
              </>
            )
          ) : (
            <button
              onClick={onConnect}
              disabled={gscLoading}
              className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-[12px] font-bold text-[#0A0F1E] transition-all disabled:opacity-60"
              style={{ background: COLORS.impressions, boxShadow: `0 0 18px ${COLORS.impressions}55` }}
            >
              <GLogo size={14} /> {gscLoading ? "Connecting…" : "Connect Google Search Console"}
            </button>
          )}
        </div>
      </div>

      {gscError && !isDemoMode() && (
        <div className="mt-3 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-[12px] text-red-300">
          {gscError}
        </div>
      )}

      {/* Body states */}
      {gscIsConnected && !gscPropertySelected ? (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-yellow-500/30 bg-yellow-500/10 px-4 py-3 text-[12.5px]">
          <div className="font-semibold text-white">Please select your GSC property</div>
          <button
            onClick={onSelectProperty}
            className="rounded-full bg-white/10 px-3 py-1.5 text-[11.5px] font-bold text-white hover:bg-white/15"
          >
            Open picker
          </button>
        </div>
      ) : gscIsConnected && gscPropertySelected && gscData ? (
        <>
          {/* TILES */}
          <div className="mt-4 grid grid-cols-2 gap-2 sm:mt-5 sm:gap-4 lg:grid-cols-4">
            <MetricTile
              metricKey="impressions"
              label="Total Impressions"
              Icon={Eye}
              color={COLORS.impressions}
              value={gscData.impressions}
              format={(n) => Math.round(n).toLocaleString()}
              trend={trends.impressions}
              sparkData={sparkData("impressions")}
              active={visible.impressions}
              onToggle={() => setVisible((v) => ({ ...v, impressions: !v.impressions }))}
            />
            <MetricTile
              metricKey="clicks"
              label="Total Clicks"
              Icon={MousePointerClick}
              color={COLORS.clicks}
              value={gscData.clicks}
              format={(n) => Math.round(n).toLocaleString()}
              trend={trends.clicks}
              sparkData={sparkData("clicks")}
              active={visible.clicks}
              onToggle={() => setVisible((v) => ({ ...v, clicks: !v.clicks }))}
            />
            <MetricTile
              metricKey="ctr"
              label="CTR"
              Icon={Percent}
              color={COLORS.ctr}
              value={gscData.ctr * 100}
              format={(n) => `${n.toFixed(2)}%`}
              trend={trends.ctr}
              sparkData={sparkData("ctr").map((d) => ({ x: d.x, y: d.y * 100 }))}
              active={visible.ctr}
              onToggle={() => setVisible((v) => ({ ...v, ctr: !v.ctr }))}
            />
            <MetricTile
              metricKey="position"
              label="Avg. Position"
              Icon={Crosshair}
              color={COLORS.position}
              value={gscData.position}
              format={(n) => n.toFixed(1)}
              trend={trends.position}
              sparkData={sparkData("position")}
              active={visible.position}
              onToggle={() => setVisible((v) => ({ ...v, position: !v.position }))}
            />
          </div>

          {/* TIME TOGGLE */}
          <div className="mt-4 flex justify-center sm:mt-6">
            <div
              className="inline-flex items-center gap-1 rounded-full p-1"
              style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}
            >
              {(["24h", "7d", "28d", "90d"] as const).map((opt) => {
                const active = gscDateRange === opt;
                return (
                  <button
                    key={opt}
                    onClick={() => onDateRangeChange(opt)}
                    disabled={gscLoading}
                    className="rounded-full px-3 py-1.5 text-[11px] sm:px-4 sm:text-[12px] font-bold transition-all disabled:opacity-60"
                    style={{
                      background: active ? COLORS.clicks : "transparent",
                      color: active ? "#fff" : "rgba(255,255,255,0.65)",
                      boxShadow: active ? `0 0 16px ${COLORS.clicks}66` : "none",
                    }}
                  >
                    {opt}
                  </button>
                );
              })}
            </div>
          </div>

          {/* MAIN CHART */}
          {chartData.length > 0 && (
            <div data-tour-id="gsc-chart" className="mt-3 w-full h-[180px] sm:mt-4 sm:h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 12, right: 8, left: 0, bottom: 8 }}>
                  <defs>
                    {Object.entries(COLORS).map(([k, c]) => (
                      <linearGradient key={k} id={`grad-${k}`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={c} stopOpacity={0.25} />
                        <stop offset="100%" stopColor={c} stopOpacity={0} />
                      </linearGradient>
                    ))}
                  </defs>
                  <CartesianGrid stroke="#ffffff" strokeOpacity={0.06} vertical={false} />
                  <XAxis
                    dataKey="label"
                    tick={{ fill: "rgba(255,255,255,0.45)", fontSize: 10 }}
                    tickLine={false}
                    axisLine={false}
                    minTickGap={28}
                  />
                  <YAxis
                    yAxisId="left"
                    tick={{ fill: COLORS.impressions, fontSize: 10 }}
                    tickLine={false}
                    axisLine={false}
                    width={42}
                  />
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    tick={{ fill: COLORS.ctr, fontSize: 10 }}
                    tickLine={false}
                    axisLine={false}
                    width={42}
                  />
                  <Tooltip
                    cursor={{ stroke: "rgba(255,255,255,0.25)", strokeWidth: 1, strokeDasharray: "4 4" }}
                    content={({ active, payload, label }) => {
                      if (!active || !payload?.length) return null;
                      const iso = (payload[0]?.payload as any)?.date as string;
                      const dateStr = iso
                        ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
                        : (label as string);
                      const labels: Record<string, { name: string; color: string; fmt: (v: any) => string }> = {
                        impressions: { name: "Impressions", color: COLORS.impressions, fmt: (v) => Number(v).toLocaleString() },
                        clicks: { name: "Clicks", color: COLORS.clicks, fmt: (v) => Number(v).toLocaleString() },
                        ctr: { name: "CTR", color: COLORS.ctr, fmt: (v) => `${Number(v).toFixed(2)}%` },
                        position: { name: "Position", color: COLORS.position, fmt: (v) => Number(v).toFixed(1) },
                      };
                      return (
                        <div
                          className="rounded-xl px-3 py-2.5 text-[11.5px] shadow-2xl"
                          style={{ background: "rgba(10,15,30,0.95)", border: "1px solid rgba(255,255,255,0.1)", backdropFilter: "blur(8px)" }}
                        >
                          <div className="mb-1.5 font-semibold text-white">{dateStr}</div>
                          {payload.map((p: any) => {
                            const meta = labels[p.dataKey];
                            if (!meta) return null;
                            return (
                              <div key={p.dataKey} className="flex items-center justify-between gap-6 py-0.5">
                                <span className="flex items-center gap-1.5">
                                  <span className="h-2 w-2 rounded-full" style={{ background: meta.color }} />
                                  <span className="text-white/70">{meta.name}</span>
                                </span>
                                <span className="font-bold tabular-nums" style={{ color: meta.color }}>
                                  {meta.fmt(p.value)}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      );
                    }}
                  />
                  {visible.impressions && (
                    <Line yAxisId="left" type="monotone" dataKey="impressions" stroke={COLORS.impressions} strokeWidth={2.2} dot={false}
                      activeDot={{ r: 5, stroke: COLORS.impressions, strokeWidth: 2, fill: "#0A0F1E" }}
                      isAnimationActive animationDuration={1100} />
                  )}
                  {visible.clicks && (
                    <Line yAxisId="left" type="monotone" dataKey="clicks" stroke={COLORS.clicks} strokeWidth={2.2} dot={false}
                      activeDot={{ r: 5, stroke: COLORS.clicks, strokeWidth: 2, fill: "#0A0F1E" }}
                      isAnimationActive animationDuration={1100} />
                  )}
                  {visible.ctr && (
                    <Line yAxisId="right" type="monotone" dataKey="ctr" stroke={COLORS.ctr} strokeWidth={2.2} dot={false}
                      activeDot={{ r: 5, stroke: COLORS.ctr, strokeWidth: 2, fill: "#0A0F1E" }}
                      isAnimationActive animationDuration={1100} />
                  )}
                  {visible.position && (
                    <Line yAxisId="right" type="monotone" dataKey="position" stroke={COLORS.position} strokeWidth={2.2} dot={false}
                      activeDot={{ r: 5, stroke: COLORS.position, strokeWidth: 2, fill: "#0A0F1E" }}
                      isAnimationActive animationDuration={1100} />
                  )}
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* BOTTOM TOGGLES */}
          <div className="mt-2 grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-center sm:justify-center sm:gap-6">
            <ToggleSwitch on={visible.impressions} color={COLORS.impressions} label="Impressions"
              onChange={() => setVisible((v) => ({ ...v, impressions: !v.impressions }))} />
            <ToggleSwitch on={visible.clicks} color={COLORS.clicks} label="Clicks"
              onChange={() => setVisible((v) => ({ ...v, clicks: !v.clicks }))} />
            <ToggleSwitch on={visible.ctr} color={COLORS.ctr} label="CTR"
              onChange={() => setVisible((v) => ({ ...v, ctr: !v.ctr }))} />
            <ToggleSwitch on={visible.position} color={COLORS.position} label="Position"
              onChange={() => setVisible((v) => ({ ...v, position: !v.position }))} />
          </div>

          {/* FOOTER */}
          <div className="mt-5 flex flex-wrap items-center justify-between gap-2 border-t border-white/5 pt-3 text-[11px] text-white/50">
            <span className="inline-flex items-center gap-1.5">
              <Info size={11} /> All data is sourced directly from Google Search Console
            </span>
            <span className="inline-flex items-center gap-1.5">
              {lastUpdated && <>Last updated: <span className="text-white/75">{lastUpdated}</span></>}
              <span className="relative inline-flex h-2 w-2">
                <span className="absolute inset-0 animate-ping rounded-full" style={{ background: COLORS.impressions, opacity: 0.6 }} />
                <span className="relative inline-block h-2 w-2 rounded-full" style={{ background: COLORS.impressions, boxShadow: `0 0 8px ${COLORS.impressions}` }} />
              </span>
            </span>
          </div>
        </>
      ) : gscIsConnected && gscPropertySelected && gscLoading ? (
        <div className="py-10 text-center text-[12.5px] text-white/55">Loading Search Console data…</div>
      ) : (
        <div className="py-10 text-center text-[12.5px] text-white/55">
          Connect Google Search Console to see your impressions, clicks and rankings.
        </div>
      )}
    </div>
  );
}
